/**
 * Iter XX.242 · Cricket Graph V1 — client wrapper.
 *
 * Thin fetch layer over `/api/cricket-graph/*`. Never throws — every
 * function returns a safe fallback so the surrounding UI can render a
 * clean empty state rather than an error boundary.
 */
import api from "./api";

const _empty = {
  generated_at: null,
  user_id: null,
  identity: {},
  goal: null,
  current_focus: null,
  started_at: null,
  current_mission: null,
  baselines: [],
  progress_events: [],
  cricket_weeks: { consecutive: 0, qualifies_this_week: false, current: { qualified: false } },
  totals: { practices: 0, matches: 0, videos: 0, missions_completed: 0, baselines: 0, total: 0 },
  last_activity: null,
  patterns: { recurring: [], strengths: [], growth: [] },
  state: "first_time",
  next_step: null,
  has_content: false,
};

/** Fetch the full canonical Cricket Graph. Returns the empty shape on any failure. */
export async function fetchCricketGraph() {
  try {
    const { data } = await api.get("/cricket-graph/me");
    return data && typeof data === "object" ? data : _empty;
  } catch {
    return _empty;
  }
}

/**
 * Fast, low-cost 4-state classifier — used by the app shell to decide
 * first-time vs returning home layouts without paying the cost of the
 * full graph read on every render.
 */
export async function fetchCricketGraphState() {
  try {
    const { data } = await api.get("/cricket-graph/state");
    return (data && data.state) || "first_time";
  } catch {
    return "first_time";
  }
}

/**
 * Record a user-reported baseline. Returns `{ baseline, progress_events,
 * personal_best, previous_best, duplicate }` on success or `null` on
 * failure — callers should treat null as "silent no-op".
 */
export async function recordBaseline({ metricKey, value, total, sessionId, messageId }) {
  try {
    const { data } = await api.post("/cricket-graph/baseline", {
      metric_key: metricKey,
      value,
      total,
      source: "USER_REPORTED",
      session_id: sessionId,
      message_id: messageId,
    });
    return data;
  } catch {
    return null;
  }
}
